import type { AxiosInstance } from "axios";
import { Activity, Check, ExternalLink, Link2, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { SyncNowPanel } from "../components/SyncNowPanel.js";
import { TrackingPluginPanel } from "../components/TrackingPluginPanel.js";
import { Tabs } from "../components/ui/tabs.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { sourceMark } from "../lib/source-marks.js";
import { apiErrorMessage } from "../lib/api.js";
import {
  beginConnect,
  chooseAccounts,
  fetchIntegrations,
  fetchSyncStatus,
  PLATFORM_LABEL,
  startSync,
  syncInFlight,
  type IntegrationSummary,
  type Platform,
  type PlatformSyncStatus,
} from "../lib/integrations.js";

/** While a sync is queued or running, its progress is re-read this often. */
const SYNC_POLL_MS = 5_000;

type Section = Platform | "plugin";

/**
 * Connect Meta and Google ad accounts, and pick which accounts sync — legacy's
 * Integration screens, per store.
 *
 * THE OUTCOME BANNER reads `?connected=` from the callback redirect. The API cannot show
 * the merchant a page — it hands control back to Meta/Google, who hand it back here — so
 * the result of a connect arrives as a query param on THIS screen, not as an API response.
 *
 * THREE TABS — Meta, Google, Ecomally Plugin (ticket "Plugin Control", part 4) — one at a time, so
 * a store with many ad accounts is not one long scroll. A connect callback opens its platform's tab.
 */
export function IntegrationsScreen({
  client,
  tenantId,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  /** Named in the switch-off dialog, so an agency switching stores knows which one it is. */
  readonly storeName: string;
}): React.JSX.Element {
  const [rows, setRows] = useState<readonly IntegrationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Platform | null>(null);
  const [syncs, setSyncs] = useState<readonly PlatformSyncStatus[]>([]);
  const [syncError, setSyncError] = useState<string | null>(null);

  const outcome = useMemo(() => new URLSearchParams(window.location.search).get("connected"), []);
  const [section, setSection] = useState<Section>(() => {
    if (outcome === "google") return "google";
    // `?tab=plugin` — where a tracking warning sends the member.
    return new URLSearchParams(window.location.search).get("tab") === "plugin" ? "plugin" : "meta";
  });
  /**
   * `accounts=unavailable` means the token was stored but Google refused the account
   * listing. Without it this screen shows the connection as fine and the card below says
   * no ad accounts were found — which is a claim about the merchant's Google account that
   * we never actually established.
   */
  const accountsUnavailable = useMemo(
    () => new URLSearchParams(window.location.search).get("accounts") === "unavailable",
    [],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await fetchIntegrations(client));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load integrations."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  /**
   * Sync status is read on its own, and its failure does NOT blank the screen: connecting and
   * choosing accounts must keep working when the sync status cannot be read.
   */
  const loadSyncs = useCallback(async () => {
    try {
      setSyncs(await fetchSyncStatus(client));
      setSyncError(null);
    } catch (caught) {
      // Shown, not swallowed: the cards stay usable, the sync panel says why it is missing.
      setSyncs([]);
      setSyncError(apiErrorMessage(caught, "Could not load sync status."));
    }
  }, [client]);

  useEffect(() => {
    void loadSyncs();
  }, [loadSyncs, tenantId]);

  // Poll only while something is in flight; stop as soon as it completes or fails.
  const polling = syncs.some(syncInFlight);
  useEffect(() => {
    if (!polling) return undefined;
    const timer = window.setInterval(() => void loadSyncs(), SYNC_POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling, loadSyncs]);

  const connect = async (platform: Platform): Promise<void> => {
    setBusy(platform);
    try {
      const url = await beginConnect(client, platform);
      // Full-page navigation to the provider's consent screen — not a fetch.
      window.location.assign(url);
    } catch (caught) {
      setError(apiErrorMessage(caught, `Could not start the ${PLATFORM_LABEL[platform]} connection.`));
      setBusy(null);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Integrations</h1>
        <p className="text-muted-foreground text-sm">
          Your ad accounts, and Ecomally's tracking on {storeName}.
        </p>
      </header>

      {outcome !== null && <OutcomeBanner outcome={outcome} accountsUnavailable={accountsUnavailable} />}
      {error !== null && (
        <Card className="border-destructive/40">
          <CardContent className="text-destructive pt-0 text-sm">{error}</CardContent>
        </Card>
      )}

      <Tabs
        label="Integrations"
        value={section}
        onValueChange={setSection}
        items={[
          { value: "meta", label: <PlatformTabLabel platform="meta" row={rows?.find((r) => r.platform === "meta")} /> },
          { value: "google", label: <PlatformTabLabel platform="google" row={rows?.find((r) => r.platform === "google")} /> },
          {
            value: "plugin",
            label: (
              <>
                <Activity />
                Ecomally Plugin
              </>
            ),
          },
        ]}
      >
        {section === "plugin" ? (
          <TrackingPluginPanel client={client} storeName={storeName} />
        ) : (
          <>
            {syncError !== null && <p className="text-muted-foreground mb-2 text-xs">{syncError}</p>}
            {rows === null ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : (
              rows
                .filter((row) => row.platform === section)
                .map((row) => (
                  <PlatformCard
                    key={row.platform}
                    row={row}
                    busy={busy === row.platform}
                    sync={syncs.find((s) => s.platform === row.platform) ?? null}
                    onSync={async () => {
                      await startSync(client, row.platform);
                      await loadSyncs();
                    }}
                    onConnect={() => void connect(row.platform)}
                    onSave={async (selected) => {
                      setRows(await chooseAccounts(client, row.platform, selected));
                    }}
                  />
                ))
            )}
          </>
        )}
      </Tabs>
    </div>
  );
}

/** The platform's mark and name, with a dot once it is connected — readable without opening the tab. */
function PlatformTabLabel({
  platform,
  row,
}: {
  readonly platform: Platform;
  readonly row: IntegrationSummary | undefined;
}): React.JSX.Element {
  const { Icon, color } = sourceMark(platform);
  return (
    <>
      {Icon !== null && <Icon style={{ color }} />}
      {PLATFORM_LABEL[platform]}
      {row?.connected === true && <span aria-label="connected" className="size-1.5 rounded-full bg-emerald-500" />}
    </>
  );
}

function OutcomeBanner({
  outcome,
  accountsUnavailable,
}: {
  readonly outcome: string;
  readonly accountsUnavailable: boolean;
}): React.JSX.Element {
  const isPlatform = outcome === "meta" || outcome === "google";
  // Connected-but-unread is NOT a success banner: there is an action to take.
  const connected = isPlatform && !accountsUnavailable;
  const text = isPlatform
    ? accountsUnavailable
      ? `${PLATFORM_LABEL[outcome]} is connected, but we could not read your ad accounts just now. Nothing is wrong with your login — try Reconnect, and if it keeps happening tell support.`
      : `${PLATFORM_LABEL[outcome]} connected. Choose which accounts to sync below.`
    : outcome === "cancelled"
      ? "Connection cancelled — nothing was changed."
      : "That connection could not be completed. Please try again.";
  return (
    <Card className={connected ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" : "border-amber-300 bg-amber-50 dark:bg-amber-950/30"}>
      <CardContent className="pt-0 text-sm">{text}</CardContent>
    </Card>
  );
}

function PlatformCard({
  row,
  busy,
  sync,
  onSync,
  onConnect,
  onSave,
}: {
  readonly row: IntegrationSummary;
  readonly busy: boolean;
  readonly sync: PlatformSyncStatus | null;
  readonly onSync: () => Promise<void>;
  readonly onConnect: () => void;
  readonly onSave: (selected: readonly string[]) => Promise<void>;
}): React.JSX.Element {
  const { Icon, color } = sourceMark(row.platform);
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(row.accounts.filter((a) => a.selected).map((a) => a.id)),
  );
  const [saving, setSaving] = useState(false);

  // A fresh summary (after connect or save) replaces the local selection.
  useEffect(() => {
    setSelected(new Set(row.accounts.filter((a) => a.selected).map((a) => a.id)));
  }, [row.accounts]);

  const dirty = useMemo(() => {
    const applied = new Set(row.accounts.filter((a) => a.selected).map((a) => a.id));
    if (applied.size !== selected.size) return true;
    for (const id of selected) if (!applied.has(id)) return true;
    return false;
  }, [row.accounts, selected]);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2">
          {Icon !== null && <Icon className="size-5" style={{ color }} />}
          {PLATFORM_LABEL[row.platform]}
          {row.connected ? (
            <Badge variant="outline" className="gap-1 border-emerald-400 text-emerald-600">
              <Check className="size-3" /> Connected
            </Badge>
          ) : (
            <Badge variant="secondary">Not connected</Badge>
          )}
        </CardTitle>
        <Button variant={row.connected ? "outline" : "default"} size="sm" onClick={onConnect} disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : row.connected ? <RefreshCw /> : <Link2 />}
          {row.connected ? "Reconnect" : "Connect"}
        </Button>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {row.connected && row.email !== null && (
          <p className="text-muted-foreground text-sm">
            Connected as {row.account ?? row.email} ({row.email})
          </p>
        )}

        {row.connected && sync !== null && <SyncNowPanel status={sync} onStart={onSync} />}

        {!row.connected ? (
          <p className="text-muted-foreground text-sm">
            Connect to import ad accounts. You choose which accounts sync after connecting.
          </p>
        ) : row.accounts.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No ad accounts were found on this login. Reconnect with an account that has ad access.
          </p>
        ) : (
          <>
            <p className="text-sm font-medium">Accounts to sync</p>
            <ul className="flex flex-col gap-1">
              {row.accounts.map((account) => (
                <li key={account.id}>
                  <label className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm">
                    <input
                      type="checkbox"
                      className="size-4"
                      checked={selected.has(account.id)}
                      onChange={(event) => {
                        const next = new Set(selected);
                        if (event.target.checked) next.add(account.id);
                        else next.delete(account.id);
                        setSelected(next);
                      }}
                    />
                    <span className="min-w-0 flex-1 truncate">{account.name ?? account.id}</span>
                    <span className="text-muted-foreground text-xs">{account.id}</span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={!dirty || saving}
                onClick={() => {
                  setSaving(true);
                  void onSave([...selected]).finally(() => setSaving(false));
                }}
              >
                {saving && <Loader2 className="animate-spin" />}
                Save selection
              </Button>
              {/*
                Deselecting keeps history. A merchant who un-ticks an account today does not
                lose last quarter's spend — the row is marked deselected, not deleted, so
                closed months are never silently rewritten.
              */}
              <span className="text-muted-foreground text-xs">
                <ExternalLink className="mr-1 inline size-3" />
                Unticking an account keeps its past data; it just stops syncing.
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
